import { Wallet, Package, ShoppingCart, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useMyLicensePacks, useOperatorWallet, useCreateOrder, useVerifyPayment } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { SectionHeader, Spinner } from '../../components/ui';
import PendingPaymentsPanel from '../../components/billing/PendingPaymentsPanel';
import { CHECKOUT_ORDER_INVALID_MESSAGE, isValidCheckoutOrder, loadRazorpayCheckout } from '../../lib/razorpay';

// An interrupted or failed verification call is not evidence that Razorpay
// failed to capture the payment, so no purchase is reported as failed and no
// second order is created automatically.
const VERIFICATION_PENDING_MESSAGE =
  'Your payment has not been confirmed yet. Do not pay again — if money was debited, use "Pending payments" below to check the status.';

export default function OperatorRechargePage() {
  const user = useSessionStore((s) => s.user);
  const { data: wallet, isLoading: walletLoading } = useOperatorWallet(user?.id);
  const { data: packs = [], isLoading: packsLoading } = useMyLicensePacks();
  const { mutateAsync: createOrder } = useCreateOrder();
  const { mutateAsync: verifyPayment } = useVerifyPayment();
  const [buyingPackId, setBuyingPackId] = useState(null);
  const [buyError, setBuyError] = useState(null);
  const [buySuccess, setBuySuccess] = useState(null);
  const [buyNotice, setBuyNotice] = useState(null);

  async function handleBuyPack(pack) {
    if (buyingPackId) return;
    setBuyingPackId(pack.id);
    setBuyError(null);
    setBuySuccess(null);
    setBuyNotice(null);
    try {
      const order = await createOrder({ item_type: 'license_pack', item_id: pack.id });
      // License packs are always paid to the central AlgoSys Razorpay account.
      // The browser still uses the key the backend returned for this order and
      // has no static key to fall back to, so an Operator's own merchant key
      // can never be used for a license-pack purchase.
      if (!isValidCheckoutOrder(order)) {
        setBuyError(CHECKOUT_ORDER_INVALID_MESSAGE);
        setBuyingPackId(null);
        return;
      }
      let Razorpay;
      try {
        Razorpay = await loadRazorpayCheckout();
      } catch {
        setBuyError('Payment provider could not be loaded. Check your connection and try again.');
        setBuyingPackId(null);
        return;
      }
      const rzp = new Razorpay({
        key: order.key_id,
        order_id: order.razorpay_order_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'ALGOSYS',
        description: `${pack.name} — ${pack.license_qty} licenses`,
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            setBuySuccess(`${pack.license_qty} licenses added to your wallet.`);
            // wallet query auto-invalidated by useVerifyPayment onSuccess
          } catch {
            setBuyNotice(VERIFICATION_PENDING_MESSAGE);
          } finally {
            setBuyingPackId(null);
          }
        },
        modal: {
          ondismiss: () => {
            setBuyingPackId(null);
            setBuyNotice('Checkout was closed. If you completed a payment, use "Pending payments" below to check whether it has been confirmed.');
          },
        },
      });
      rzp.open();
    } catch (err) {
      setBuyError(err.message || 'Could not initiate payment. Please try again.');
      setBuyingPackId(null);
    }
  }

  // Availability comes from the backend. `total - used` would wrongly count
  // licenses that are still reserved against an unresolved payment.
  const available = wallet?.available_licenses ?? 0;
  const reserved = wallet?.reserved_licenses ?? 0;

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <SectionHeader title="Recharge" />

      {/* Wallet summary */}
      <div className="border border-bg-border rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-txt-primary mb-4">
          <Wallet size={16} />
          License Wallet
        </div>
        {walletLoading ? (
          <Spinner />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {[
              { label: 'Total', value: wallet?.total_licenses ?? 0, cls: 'text-txt-primary' },
              { label: 'Used', value: wallet?.used_licenses ?? 0, cls: 'text-warn' },
              { label: 'Reserved', value: reserved, cls: reserved > 0 ? 'text-info' : 'text-txt-muted' },
              { label: 'Available', value: available, cls: available > 0 ? 'text-bull' : 'text-bear' },
            ].map(({ label, value, cls }) => (
              <div key={label} className="bg-bg-surface rounded p-3">
                <div className={`text-2xl font-bold ${cls}`}>{value}</div>
                <div className="text-2xs text-txt-muted mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        )}
        {reserved > 0 && (
          <p className="text-2xs text-txt-muted mt-2">
            Reserved licenses are held against payments that are not confirmed yet and are excluded from Available.
          </p>
        )}
      </div>

      {/* Pending license-pack payments (owner-scoped, paginated by the backend) */}
      <PendingPaymentsPanel itemType="license_pack" title="Pending payments" />

      {/* Available packs */}
      <div>
        <div className="flex items-center gap-2 text-sm font-medium text-txt-primary mb-3">
          <Package size={15} />
          Available License Packs
        </div>

        {buyError && (
          <div className="mb-3 text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">
            {buyError}
          </div>
        )}
        {buyNotice && (
          <div className="mb-3 text-xs text-warn bg-warn/5 border border-warn/30 rounded px-3 py-2">
            {buyNotice}
          </div>
        )}
        {buySuccess && (
          <div className="mb-3 text-xs text-bull bg-bull/5 border border-bull/30 rounded px-3 py-2">
            {buySuccess}
          </div>
        )}

        {packsLoading ? (
          <Spinner />
        ) : packs.length === 0 ? (
          <p className="text-xs text-txt-muted">No license packs available. Contact your administrator.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {packs.map((pack) => {
              const isBuying = buyingPackId === pack.id;
              return (
                <div
                  key={pack.id}
                  className="border border-bg-border rounded-lg p-4 bg-bg-surface/30 flex flex-col gap-3"
                >
                  <div>
                    <div className="text-sm font-medium text-txt-primary">{pack.name}</div>
                    {pack.description && (
                      <div className="text-2xs text-txt-muted mt-0.5">{pack.description}</div>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="space-y-0.5">
                      <div className="text-txt-muted">
                        Licenses: <span className="text-txt-primary font-medium">{pack.license_qty}</span>
                      </div>
                      <div className="text-txt-muted">
                        Price: <span className="text-txt-primary font-medium">₹{pack.price}</span>
                      </div>
                    </div>
                    <button
                      className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
                      disabled={!!buyingPackId}
                      onClick={() => handleBuyPack(pack)}
                    >
                      {isBuying ? <Loader2 size={12} className="animate-spin" /> : <ShoppingCart size={12} />}
                      {isBuying ? 'Opening...' : 'Buy'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
